// Application-wide constants

import type { UserType, ZoomRecommendation } from '@/types';

// Pricing tiers
export const PRICING_TIERS: Record<
  UserType,
  {
    name: string;
    monthlyFee: number;
    tilesPerMonth: number;
    pricePerThousandTiles: number;
    features: string[];
    rolloverLimit?: number;
    popular?: boolean;
  }
> = {
  casual: {
    name: 'Casual',
    monthlyFee: 0,
    tilesPerMonth: 0,
    pricePerThousandTiles: 0.4,
    features: ['Pay as you go', 'No commitment', 'Standard support', 'All zoom levels']
  },
  basic: {
    name: 'Basic',
    monthlyFee: 50,
    tilesPerMonth: 100000,
    pricePerThousandTiles: 0.5,
    rolloverLimit: 50000,
    features: [
      '100k tokens/month',
      'Rollover up to 50k',
      '$0.50 per 1k over quota',
      'Priority support',
      'Download history'
    ]
  },
  pro: {
    name: 'Pro',
    monthlyFee: 150,
    tilesPerMonth: 500000,
    pricePerThousandTiles: 0.4,
    popular: true,
    features: [
      '500k tokens/month',
      'Unlimited rollover',
      '$0.40 per 1k over quota',
      'Premium support',
      'Download history',
      'Priority generation'
    ]
  },
  enterprise: {
    name: 'Enterprise',
    monthlyFee: 500,
    tilesPerMonth: 2000000,
    pricePerThousandTiles: 0.3,
    features: [
      '2M tokens/month',
      'Unlimited rollover',
      '$0.30 per 1k over quota',
      '24/7 dedicated support',
      'Custom API access',
      'Bulk downloads',
      'White-label options'
    ]
  }
};

export const BASE_MBTILES_FEE = 50; // USD

// Zoom level recommendations
export const ZOOM_RECOMMENDATIONS: ZoomRecommendation[] = [
  {
    useCase: 'Ocean Passage',
    description: 'Long-distance ocean voyages, general navigation',
    recommendedRange: { min: 6, max: 10 },
    icon: '🌊',
    matchScore: 0
  },
  {
    useCase: 'Coastal Cruising',
    description: 'Medium-range coastal navigation, island hopping',
    recommendedRange: { min: 10, max: 14 },
    icon: '⛵',
    matchScore: 0
  },
  {
    useCase: 'Harbor & Approaches',
    description: 'Harbor entry, anchorages, marina approaches',
    recommendedRange: { min: 14, max: 16 },
    icon: '⚓',
    matchScore: 0
  },
  {
    useCase: 'Detail & Docking',
    description: 'Tight navigation, docking, detailed harbor charts',
    recommendedRange: { min: 16, max: 18 },
    icon: '🚢',
    matchScore: 0
  }
];

// Tile size estimates (average bytes per tile by zoom level)
export const TILE_SIZE_ESTIMATES: Record<number, number> = {
  0: 12000,
  1: 12000,
  2: 13000,
  3: 14000,
  4: 15000,
  5: 16000,
  6: 17000,
  7: 18000,
  8: 19000,
  9: 20000,
  10: 21000,
  11: 22000,
  12: 23000,
  13: 24000,
  14: 25000,
  15: 26000,
  16: 27000,
  17: 28000,
  18: 29000,
  19: 30000,
  20: 31000
};

// Validation limits
export const LIMITS = {
  MAX_AOI_AREA_KM2: 500000, // 500,000 km²
  MAX_FILE_SIZE_GB: 10,
  MAX_TOTAL_TILES: 10000000, // 10 million tiles
  MIN_ZOOM: 0,
  MAX_ZOOM: 20,
  EMAIL_DELIVERY_THRESHOLD_MB: 100 // Files > 100MB get emailed
};

// Generation time estimates (tiles per second)
export const GENERATION_SPEED = {
  FAST: 1000, // tiles/sec for modern machines
  MEDIUM: 500,
  SLOW: 200
};

// Map configuration
export const MAP_CONFIG = {
  DEFAULT_CENTER: { lng: 177.0996, lat: -17.6733 }, // Mana Island, Fiji
  DEFAULT_ZOOM: 12,
  MIN_ZOOM: 0,
  MAX_ZOOM: 20,
  TILE_SIZE: 256
};

// Basemap URLs for different imagery sources
export const BASEMAP_URLS = {
  GOOGLE_SATELLITE: 'https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
  SENTINEL2: 'https://tiles.maps.eox.at/wmts/1.0.0/s2cloudless-2021_3857/default/GoogleMapsCompatible/{z}/{y}/{x}.jpg',
  ESRI_SATELLITE: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
};

// Available imagery sources for tile generation
export const IMAGERY_SOURCES = {
  sentinel2: {
    id: 'sentinel2',
    name: 'Sentinel-2 Cloudless',
    description: 'European Space Agency Sentinel-2 satellite imagery (2021 mosaic)',
    attribution: 'Sentinel-2 Cloudless by EOX',
    previewUrl: BASEMAP_URLS.SENTINEL2
  },
  google: {
    id: 'google',
    name: 'Google Satellite',
    description: 'High-resolution Google satellite imagery',
    attribution: 'Google Satellite Imagery',
    previewUrl: BASEMAP_URLS.GOOGLE_SATELLITE
  }
};

// Default imagery source
export const DEFAULT_IMAGERY_SOURCE = 'sentinel2';

// Animation durations (ms)
export const ANIMATION_DURATION = {
  FAST: 200,
  NORMAL: 300,
  SLOW: 500
};

// Debounce delays (ms)
export const DEBOUNCE_DELAY = {
  SEARCH: 300,
  TILE_CALC: 500,
  AOI_UPDATE: 300
};

// Legal links
export const LEGAL_LINKS = {
  TERMS_OF_SERVICE: '/legal/terms',
  PRIVACY_POLICY: '/legal/privacy',
  REFUND_POLICY: '/legal/refunds',
  COOKIE_POLICY: '/legal/cookies'
};

// Support
export const SUPPORT = {
  EMAIL: 'support@vortexmarine.com',
  PHONE: '+1 (555) 123-4567',
  LIVE_CHAT_URL: 'https://chat.vortexmarine.com'
};

// Social media
export const SOCIAL_LINKS = {
  TWITTER: 'https://twitter.com/vortexmarine',
  FACEBOOK: 'https://facebook.com/vortexmarine',
  INSTAGRAM: 'https://instagram.com/vortexmarine',
  LINKEDIN: 'https://linkedin.com/company/vortexmarine'
};

// Referral program
export const REFERRAL_BONUS_TILES = 50000; // 50k bonus tiles per referral

// Email templates
export const EMAIL_TEMPLATES = {
  DOWNLOAD_READY: 'download-ready',
  PAYMENT_RECEIPT: 'payment-receipt',
  WELCOME: 'welcome',
  SUBSCRIPTION_UPDATED: 'subscription-updated'
};
